// Register the custom elements used by the injected product details.
// (Also loaded globally via scripts.liquid; importing here makes the dependency
// explicit and guarantees registration wherever the section is placed.)
import '@theme/view-event-elements';
import '@theme/variant-picker';
import '@theme/product-siblings-swatches';
import '@theme/fit-switcher';
import '@theme/product-form';

/**
 * Shared, page-wide fetch cache keyed by product URL. Promoted to module scope
 * (rather than a per-instance field) so a product appearing in more than one
 * look on the same page is fetched only once. Values are the in-flight/settled
 * fetch promise, so concurrent callers reuse a single request. A failed fetch
 * removes its entry so a later click can retry.
 * @type {Map<string, Promise<Element | null>>}
 */
const productCache = new Map();

/**
 * <shop-the-look-products> — the interactive contents of a look's "Shop the
 * look" drawer.
 *
 * Layout (built in the block markup):
 *   - Left pane: a carousel of the selected product's images (+ the look badge).
 *   - Right pane: title, a thumbnail carousel of the look's products, and the
 *     selected product's details (variant picker, price, add-to-cart, ...).
 *
 * Selecting a thumbnail rebuilds the left image carousel and drops the selected
 * product's `.product-details` — fetched with the Section Rendering API, like
 * quick-add — into the right pane. All products are preloaded eagerly on
 * connect and cached, so switching is instant.
 *
 * Expected hooks:
 *   [data-product-url]     — per thumbnail: the product URL to fetch
 *   [data-featured-image]  — per thumbnail: a fallback image if the fetch fails
 *   [data-stl-gallery]     — the left-pane carousel track (images go here)
 *   [data-stl-gallery-dots]— the left-pane dot container
 *   [data-stl-prev/next]   — the left-pane arrows
 *   [data-stl-content]     — the <product-component> the details land in
 */
class ShopTheLookProducts extends HTMLElement {
  /** The URL of the product currently shown (guards out-of-order fetches). */
  #currentUrl = '';
  /** Whether the first-open init (preload + select) has run. */
  #initialized = false;

  /**
   * The look whose product details are in the document. Only one may be at a time:
   * every look injects the same `shop-the-look-product` section, and that section's
   * block ids are fixed, so two looks holding a product at once put two elements under
   * the same id in the page — the size guide's own drawer among them. Both guide links
   * find theirs with `getElementById`, which answers with the first in document order:
   * a drawer sitting inside a look the shopper has closed, where opening it shows
   * nothing at all.
   *
   * @type {ShopTheLookProducts | null}
   */
  static #showing = null;
  /** @type {HTMLElement[]} */
  #slides = [];

  connectedCallback() {
    this.thumbs = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-product-url]')));
    this.gallery = this.querySelector('[data-stl-gallery]');
    this.galleryDots = this.querySelector('[data-stl-gallery-dots]');
    this.content = this.querySelector('[data-stl-content]');
    this.prevButton = this.querySelector('[data-stl-prev]');
    this.nextButton = this.querySelector('[data-stl-next]');
    this.moreLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-stl-more]'));

    if (!this.thumbs.length || !this.content) return;

    this.tablist = this.querySelector('[role="tablist"]');

    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => this.#select(thumb));
    }

    // ARIA tab pattern: arrow keys move between thumbnails (roving tabindex),
    // Home/End jump to the first/last.
    this.tablist?.addEventListener('keydown', this.#onThumbsKeydown);

    this.prevButton?.addEventListener('click', () => this.#step(-1));
    this.nextButton?.addEventListener('click', () => this.#step(1));
    this.gallery?.addEventListener('scroll', this.#onGalleryScroll, { passive: true });
    this.content.addEventListener('click', this.#onContentClick);

    // Defer fetching until the drawer is first opened. Closed dialogs are still
    // in the DOM at page load, so preloading here would fire a Section
    // Rendering API request per product for every drawer on the page — most of
    // which the visitor never opens. The ancestor <theme-drawer> emits
    // `theme-drawer:open`; init once, on first open.
    this.drawer = this.closest('theme-drawer');

    // Before the first-open handler below, so the very first open hands the look over
    // before anything is injected into it.
    this.drawer?.addEventListener('theme-drawer:open', this.#onDrawerOpen);

    if (this.drawer && !this.drawer.isOpen) {
      this.drawer.addEventListener('theme-drawer:open', this.#onFirstOpen);
    } else {
      this.#init();
    }
  }

  disconnectedCallback() {
    this.gallery?.removeEventListener('scroll', this.#onGalleryScroll);
    this.content?.removeEventListener('click', this.#onContentClick);
    this.tablist?.removeEventListener('keydown', this.#onThumbsKeydown);
    this.drawer?.removeEventListener('theme-drawer:open', this.#onFirstOpen);
    this.drawer?.removeEventListener('theme-drawer:open', this.#onDrawerOpen);

    if (ShopTheLookProducts.#showing === this) ShopTheLookProducts.#showing = null;
  }

  /**
   * Hands the details pane over to the look being opened, and takes it off whichever
   * look had it — see `#showing` for why only one may hold it.
   *
   * On open rather than on close, so the look the shopper is reading is never the one
   * emptied. Costs nothing to undo: `#load` caches by URL, so a look they come back to
   * is filled again from memory without a request.
   */
  #onDrawerOpen = () => {
    const showing = ShopTheLookProducts.#showing;

    if (showing && showing !== this) showing.content?.replaceChildren();

    ShopTheLookProducts.#showing = this;

    // Empty because this look is the one that was handed over earlier. `#init` fills it
    // the first time round, and is the reason this asks rather than always re-selecting.
    if (this.#initialized && !this.content?.firstElementChild) this.#select(this.#activeThumb);
  };

  /** The thumbnail whose product the pane is showing. */
  get #activeThumb() {
    return this.thumbs.find((thumb) => thumb.classList.contains('is-active')) ?? this.thumbs[0];
  }

  /** First-open handler: unbind self, then preload + show the first product. */
  #onFirstOpen = () => {
    this.drawer?.removeEventListener('theme-drawer:open', this.#onFirstOpen);
    this.#init();
  };

  /** Preload every product (on idle), then show the first one. */
  #init() {
    if (this.#initialized) return;
    this.#initialized = true;

    const preloadAll = () => this.thumbs.forEach((thumb) => this.#load(thumb.dataset.productUrl));
    if ('requestIdleCallback' in window) {
      requestIdleCallback(preloadAll);
    } else {
      preloadAll();
    }

    this.#select(this.thumbs[0]);
  }

  /**
   * Fetch a product's content once and cache the promise (shared across every
   * look on the page). Concurrent callers for the same URL reuse one request.
   * @param {string | undefined} url
   * @returns {Promise<Element | null>}
   */
  #load(url) {
    if (!url) return Promise.resolve(null);

    let pending = productCache.get(url);
    if (!pending) {
      pending = this.#fetch(url);
      productCache.set(url, pending);
    }
    return pending;
  }

  /**
   * @param {string} url
   * @returns {Promise<Element | null>}
   */
  async #fetch(url) {
    try {
      // Fetch only the dedicated Shop-the-look product section, not the whole
      // PDP (Section Rendering API).
      const endpoint = new URL(url, window.location.origin);
      endpoint.searchParams.set('section_id', 'shop-the-look-product');

      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return doc.querySelector('[data-product-grid-content]');
    } catch (error) {
      // Reset so a later click can retry.
      productCache.delete(url);
      console.warn('[shop-the-look] failed to load product', url, error);
      return null;
    }
  }

  /**
   * Handles a look-thumbnail click: mark it active, then show its product.
   * @param {HTMLButtonElement} thumb
   */
  #select(thumb) {
    for (const other of this.thumbs) {
      const active = other === thumb;
      other.classList.toggle('is-active', active);
      other.setAttribute('aria-selected', active ? 'true' : 'false');
      // Roving tabindex: only the active tab is in the tab order.
      other.tabIndex = active ? 0 : -1;
    }
    // Point the panel at the tab that now labels it.
    if (thumb.id) this.content?.setAttribute('aria-labelledby', thumb.id);
    this.#showProduct(thumb.dataset.productUrl, thumb.dataset.featuredImage);
  }

  /**
   * Arrow-key navigation for the thumbnail tablist (roving tabindex).
   * @param {KeyboardEvent} event
   */
  #onThumbsKeydown = (event) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const current = this.thumbs.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (current === -1) return;

    let next;
    if (event.key in keys) {
      next = (current + keys[event.key] + this.thumbs.length) % this.thumbs.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = this.thumbs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const target = this.thumbs[next];
    this.#select(target);
    target.focus();
  };

  /**
   * Click delegation for the colour swatches (sibling products), which are
   * injected fresh with each product. Loads the sibling in place instead of
   * letting the link's default navigation close the drawer.
   * @param {Event} event
   */
  #onContentClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const sibling = /** @type {HTMLElement | null} */ (target.closest?.('[data-sibling-swatch]'));
    if (!sibling || !this.content?.contains(sibling)) return;

    // Stop the click reaching the theme-drawer's backdrop/outside-click handler,
    // which would otherwise close the drawer. Scoped to swatch clicks only, so
    // variant-picker / add-to-cart clicks inside the drawer still work.
    event.preventDefault();
    event.stopPropagation();
    // The live `href` rather than `data-product-url`: product-siblings-swatches.js
    // points it at the size and fit the shopper already picked, and the drawer loads
    // that URL through the Section Rendering API, which honours its `?variant=`.
    this.#showProduct(sibling.getAttribute('href') || sibling.dataset.productUrl);
  };

  /**
   * Loads a product URL into the drawer: rebuilds the left image carousel and
   * injects the product details on the right.
   * @param {string | null | undefined} url
   * @param {string} [fallbackImage] - shown in the carousel if the fetch fails
   */
  async #showProduct(url, fallbackImage) {
    if (!url || !this.content) return;

    this.#currentUrl = url;
    this.content.setAttribute('data-loading', '');

    // Point the "More details" button at the selected product's PDP.
    if (this.moreLink) {
      this.moreLink.href = url;
      this.moreLink.hidden = false;
    }

    const grid = await this.#load(url);

    // A newer selection happened while this fetch was in flight — drop it.
    if (this.#currentUrl !== url) return;

    this.content.removeAttribute('data-loading');
    if (!grid) {
      // Fetch failed. Clear the previous product's details so its variant
      // picker / add-to-cart can't submit the wrong product, and drop the stale
      // "More details" link. Show the fallback image in the left pane.
      this.content.replaceChildren();
      if (this.moreLink) this.moreLink.hidden = true;
      if (fallbackImage) this.#buildGallery([{ src: fallbackImage, alt: '' }]);
      return;
    }

    this.#buildGallery(this.#extractImages(grid, fallbackImage));

    const details = grid.querySelector('.product-details');
    if (details) {
      // Keep the details scoped to the <product-component> so variant selection
      // updates the price/availability. Sync the view-event payload as well.
      const payload = grid.getAttribute('view-event-payload');
      if (payload) this.content.setAttribute('view-event-payload', payload);
      this.content.replaceChildren(details.cloneNode(true));
    }
  }

  /**
   * Collects the product's image sources from the fetched media gallery.
   * @param {Element} grid
   * @param {string} [fallbackImage]
   * @returns {{ src: string, srcset: string, sizes: string, alt: string }[]}
   */
  #extractImages(grid, fallbackImage) {
    const media = grid.querySelector('.product-information__media') || grid;
    const seen = new Set();
    const images = [];

    for (const img of media.querySelectorAll('img')) {
      const src = img.getAttribute('src');
      if (!src || seen.has(src)) continue;
      seen.add(src);
      images.push({
        src,
        srcset: img.getAttribute('srcset') || '',
        sizes: img.getAttribute('sizes') || '',
        alt: img.getAttribute('alt') || '',
      });
    }

    if (!images.length && fallbackImage) {
      images.push({ src: fallbackImage, srcset: '', sizes: '', alt: '' });
    }

    return images;
  }

  /**
   * (Re)builds the left-pane image carousel.
   * @param {{ src: string, srcset?: string, sizes?: string, alt?: string }[]} images
   */
  #buildGallery(images) {
    if (!this.gallery) return;

    this.#slides = images.map((image) => {
      const slide = document.createElement('div');
      slide.className = 'stl-shop__slide';

      const img = document.createElement('img');
      img.className = 'stl-shop__slide-image';
      img.src = image.src;
      if (image.srcset) img.srcset = image.srcset;
      if (image.sizes) img.sizes = image.sizes;
      img.alt = image.alt || '';
      img.loading = 'lazy';

      slide.appendChild(img);
      return slide;
    });

    this.gallery.replaceChildren(...this.#slides);
    this.gallery.scrollTo({ left: 0, behavior: 'instant' });
    this.#buildGalleryDots();
    this.#updateGalleryControls(0);
  }

  #buildGalleryDots() {
    if (!this.galleryDots) return;

    this.dots = this.#slides.map((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'stl-shop__gallery-dot';
      // The dots are decorative (their container is aria-hidden; the arrows and
      // swipe drive navigation for AT). Keep them out of the tab order so
      // keyboard users don't land on controls their screen reader can't see.
      dot.tabIndex = -1;
      dot.addEventListener('click', () => {
        slide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
      return dot;
    });

    this.galleryDots.replaceChildren(...this.dots);
  }

  /** @param {number} direction -1 (prev) or 1 (next) */
  #step(direction) {
    if (!this.gallery || !this.#slides.length) return;
    const target = this.#slides[this.#activeIndex() + direction];
    target?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  #activeIndex() {
    if (!this.gallery || !this.#slides.length) return 0;
    const center = this.gallery.scrollLeft + this.gallery.clientWidth / 2;
    let active = 0;
    let best = Infinity;
    this.#slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - center);
      if (distance < best) {
        best = distance;
        active = index;
      }
    });
    return active;
  }

  #onGalleryScroll = () => this.#updateGalleryControls(this.#activeIndex());

  /** @param {number} active */
  #updateGalleryControls(active) {
    this.dots?.forEach((dot, index) => dot.classList.toggle('is-active', index === active));

    const single = this.#slides.length <= 1;
    for (const control of [this.prevButton, this.nextButton, this.galleryDots]) {
      control?.toggleAttribute('hidden', single);
    }
    this.prevButton?.toggleAttribute('disabled', active === 0);
    this.nextButton?.toggleAttribute('disabled', active === this.#slides.length - 1);
  }
}

if (!customElements.get('shop-the-look-products')) {
  customElements.define('shop-the-look-products', ShopTheLookProducts);
}
